import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Calendar,
  CALENDAR_MODE_LABELS,
  CALENDAR_MODES,
  WEEKDAY_SHORT_LABELS,
  formatTimePickerValue,
  type CalendarMode,
  type CalendarRangeValue,
  type CalendarWeekday,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'

const DEFAULT_VIEW = new Date(2026, 2, 1)
const DEFAULT_SELECTED = new Date(2026, 2, 2)
const DEFAULT_RANGE: CalendarRangeValue = {
  start: new Date(2026, 2, 2),
  end: new Date(2026, 2, 6),
}
const DEFAULT_PROVIDER_WEEKDAYS: CalendarWeekday[] = [1, 3, 4]
const DEFAULT_PROVIDER_DATE = new Date(2026, 2, 4)

const formatDateInputValue = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatDisplayDate = (date: Date | null): string => {
  if (!date) {
    return 'None'
  }

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const CalendarShowcase: React.FC = () => {
  const [highlightedMode, setHighlightedMode] = useState<CalendarMode>('date')
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    DEFAULT_SELECTED,
  )
  const [rangeValue, setRangeValue] =
    useState<CalendarRangeValue>(DEFAULT_RANGE)
  const [selectedMonth, setSelectedMonth] = useState(4)
  const [selectedYear, setSelectedYear] = useState(2026)
  const [viewDate, setViewDate] = useState(DEFAULT_VIEW)
  const [timeValue, setTimeValue] = useState('')
  const [darkMode, setDarkMode] = useState(false)
  const [providerWeekdays, setProviderWeekdays] = useState<CalendarWeekday[]>(
    DEFAULT_PROVIDER_WEEKDAYS,
  )
  const [providerDate, setProviderDate] = useState<Date | null>(
    DEFAULT_PROVIDER_DATE,
  )
  const [providerViewDate, setProviderViewDate] = useState(DEFAULT_VIEW)
  const sectionRefs = useRef<Partial<Record<CalendarMode, HTMLElement | null>>>(
    {},
  )

  const toggleProviderWeekday = (weekday: CalendarWeekday): void => {
    setProviderWeekdays((current) => {
      if (current.includes(weekday)) {
        return current.filter((value) => value !== weekday)
      }

      return [...current, weekday].sort((left, right) => left - right)
    })
  }

  useEffect(() => {
    if (
      providerDate &&
      providerWeekdays.length > 0 &&
      !providerWeekdays.includes(providerDate.getDay() as CalendarWeekday)
    ) {
      setProviderDate(null)
    }
  }, [providerDate, providerWeekdays])

  useEffect(() => {
    sectionRefs.current[highlightedMode]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [highlightedMode])

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Selection',
        fields: [
          {
            id: 'calendar-mode',
            label: 'Highlighted mode',
            control: (
              <select
                id="calendar-mode"
                className="ui-field-control"
                value={highlightedMode}
                onChange={(event) =>
                  setHighlightedMode(event.target.value as CalendarMode)
                }
              >
                {CALENDAR_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {CALENDAR_MODE_LABELS[mode]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'calendar-selected-date',
            label: 'Selected date',
            control: (
              <input
                id="calendar-selected-date"
                className="ui-field-control"
                type="date"
                value={selectedDate ? formatDateInputValue(selectedDate) : ''}
                onChange={(event) => {
                  if (!event.target.value) {
                    setSelectedDate(null)
                    return
                  }
                  const [year, month, day] = event.target.value
                    .split('-')
                    .map(Number)
                  const nextDate = new Date(year, month - 1, day)
                  setSelectedDate(nextDate)
                  setViewDate(new Date(year, month - 1, 1))
                }}
              />
            ),
          },
          {
            id: 'calendar-selected-month',
            label: 'Selected month',
            control: (
              <select
                id="calendar-selected-month"
                className="ui-field-control"
                value={selectedMonth}
                onChange={(event) =>
                  setSelectedMonth(Number(event.target.value))
                }
              >
                {Array.from({ length: 12 }, (_, index) => (
                  <option key={index} value={index}>
                    {new Date(2026, index, 1).toLocaleString('en-US', {
                      month: 'long',
                    })}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'calendar-selected-year',
            label: 'Selected year',
            control: (
              <input
                id="calendar-selected-year"
                className="ui-field-control"
                type="number"
                min={2019}
                max={2030}
                value={selectedYear}
                onChange={(event) =>
                  setSelectedYear(Number(event.target.value) || 2026)
                }
              />
            ),
          },
          {
            id: 'calendar-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="calendar-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
      {
        title: 'Provider override',
        fields: [
          {
            id: 'provider-weekdays',
            label: 'Provider work days',
            control: (
              <div className="calendar-showcase__weekday-toggles">
                {WEEKDAY_SHORT_LABELS.map((label, index) => {
                  const weekday = index as CalendarWeekday

                  return (
                    <label
                      key={label}
                      className="calendar-showcase__weekday-toggle"
                    >
                      <input
                        type="checkbox"
                        checked={providerWeekdays.includes(weekday)}
                        onChange={() => toggleProviderWeekday(weekday)}
                      />
                      <span>{label}</span>
                    </label>
                  )
                })}
              </div>
            ),
          },
        ],
      },
    ],
    [
      highlightedMode,
      providerWeekdays,
      selectedDate,
      selectedMonth,
      selectedYear,
      darkMode,
    ],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Calendar"
        description="Date, range, month, year, and date & time pickers with navigation and selection styles."
        stageClassName="ui-preview__stage--wide calendar-showcase__stage-wrap"
      >
        <div
          className={`calendar-showcase__sheet${
            darkMode ? ' calendar-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              {CALENDAR_MODE_LABELS[highlightedMode]} picker with panel
              selection values. In date modes, click the month or year in the
              header to switch panels.
            </p>
            <Calendar
              mode={highlightedMode}
              value={selectedDate}
              rangeValue={rangeValue}
              month={selectedMonth}
              year={selectedYear}
              viewDate={viewDate}
              timeValue={timeValue}
              darkMode={darkMode}
              onChange={setSelectedDate}
              onRangeChange={setRangeValue}
              onMonthChange={(month, year) => {
                setSelectedMonth(month)
                setSelectedYear(year)
              }}
              onYearChange={setSelectedYear}
              onViewDateChange={setViewDate}
              onTimeChange={(time) =>
                setTimeValue(time ? formatTimePickerValue(time) : '')
              }
            />
          </section>

          {CALENDAR_MODES.map((mode) => (
            <section
              key={mode}
              ref={(element) => {
                sectionRefs.current[mode] = element
              }}
              className={`calendar-showcase__section${
                mode === highlightedMode
                  ? ' calendar-showcase__section--selected'
                  : ''
              }`}
            >
              <h3 className="calendar-showcase__section-title">
                {CALENDAR_MODE_LABELS[mode]}
              </h3>
              <Calendar
                mode={mode}
                value={selectedDate}
                rangeValue={rangeValue}
                month={selectedMonth}
                year={selectedYear}
                viewDate={viewDate}
                timeValue={timeValue}
                darkMode={darkMode}
                onChange={setSelectedDate}
                onRangeChange={setRangeValue}
                onMonthChange={(month, year) => {
                  setSelectedMonth(month)
                  setSelectedYear(year)
                }}
                onYearChange={setSelectedYear}
                onViewDateChange={setViewDate}
                onTimeChange={(time) =>
                  setTimeValue(time ? formatTimePickerValue(time) : '')
                }
              />
            </section>
          ))}

          <section className="calendar-showcase__example">
            <div className="calendar-showcase__example-copy">
              <h3 className="calendar-showcase__section-title">
                Provider schedule override
              </h3>
              <p className="calendar-showcase__example-description">
                Provider 1 works Mon, Wed, and Thu. Tuesday and Friday dates are
                grayed out and not selectable. Use the Provider override panel
                to change which weekdays are available.
              </p>
              <p className="calendar-showcase__example-selection">
                Selected: {formatDisplayDate(providerDate)}
              </p>
            </div>
            <Calendar
              mode="date"
              value={providerDate}
              viewDate={providerViewDate}
              allowedWeekdays={providerWeekdays}
              darkMode={darkMode}
              onChange={setProviderDate}
              onViewDateChange={setProviderViewDate}
            />
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="Calendar" sections={propertySections} />
    </>
  )
}

export default CalendarShowcase
